/**
 * How the value of a scanned element's field reaches the operator.
 *
 * Sun sends every value as a string and stays out of presentation: an enum as its own token, a date as
 * a raw java.sql.Timestamp, everything else as it is stored. Which is right — the backend cannot know
 * the operator's language. It does mean the card is the one that has to make three of them readable,
 * and it decides by the SHAPE of the value, never by a list of labels: a field Sun adds tomorrow must
 * read correctly the same day, with no release here.
 *
 * Anything that does not match a shape is rendered exactly as it arrived.
 */

// An enum token: WORKING, PENDING_VALIDATION, LAUNCHED. These are dictionary keys all over Jupiter —
// the part history header translates the very same ones — so a card showing them raw would spell a
// status differently from the rest of the app. A part name ('Bracket'), a code ('P-000051',
// 'AL7075-T6') or a quantity ('2.0') never matches, so it is never looked up.
const ENUM_VALUE = /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/

/**
 * java.sql.Timestamp.toString(): '2026-09-15 00:00:00.0'. A space instead of the 'T', a one-digit
 * fraction, and no zone at all.
 *
 * Deliberately NOT handed to `new Date(text)`: that string is outside the format the spec requires
 * engines to parse, so it works in Chrome and fails in other engines — the kind of bug that only
 * shows up on somebody else's terminal. Parsed by parts instead, and read as local time because a
 * Timestamp carries no zone to convert from.
 */
const SQL_TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/

/**
 * @param {string} text
 * @returns {{date?: Date, hasTime?: boolean}} empty when the text is not a timestamp
 */
export const readTimestamp = (text) => {
  const match = SQL_TIMESTAMP.exec(text)
  if (!match) return {}

  const [year, month, day, hours, minutes, seconds] = match.slice(1).map(Number)
  const date = new Date(year, month - 1, day, hours, minutes, seconds)

  // A Date constructor rolls February 31st over into March instead of refusing it. A rolled-over date
  // is worse than an unformatted one: it reads as a real target date that nobody ever set.
  const rolledOver = date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  if (rolledOver) return {}

  // Midnight on a target date is the absence of a time, not a time: showing '00:00' invites the
  // operator to read a deadline into it.
  return { date, hasTime: hours + minutes + seconds > 0 }
}

/**
 * @param {*} value as Sun sent it
 * @param {{t_: function, formatDate: function, formatDateTime: function}} presenters
 * @returns {string} what to render
 */
export const displayedHeaderValue = (value, { t_, formatDate, formatDateTime }) => {
  const text = String(value ?? '')
  if (ENUM_VALUE.test(text)) return t_(text)

  const { date, hasTime } = readTimestamp(text)
  if (date) return hasTime ? formatDateTime(date) : formatDate(date)

  return text
}
